// components/common/ErrorBoundary/ErrorBoundary.tsx
/**
 * ErrorBoundary - React Error Boundary component
 *
 * Catches JavaScript errors in child components and displays
 * a fallback UI instead of crashing the entire application.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <ComponentThatMightError />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary severity="page" onError={logError}>
 *     <PageComponent />
 *   </ErrorBoundary>
 */
import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorFallback, ErrorSeverity } from './ErrorFallback';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Severity level affects fallback UI styling */
  severity?: ErrorSeverity;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Custom fallback render function */
  fallbackRender?: (props: {
    error: Error;
    resetError: () => void;
  }) => ReactNode;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Custom error title */
  title?: string;
  /** Custom error message */
  message?: string;
  /** Key to reset the boundary (change to reset) */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary Class Component
 *
 * Note: Error boundaries must be class components in React.
 * There is no hook equivalent for componentDidCatch.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state if resetKey changes
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.resetError();
    }
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const {
      children,
      fallback,
      fallbackRender,
      severity = 'section',
      title,
      message,
    } = this.props;

    if (hasError && error) {
      // Custom fallback render function
      if (fallbackRender) {
        return fallbackRender({ error, resetError: this.resetError });
      }

      // Custom fallback component
      if (fallback) {
        return fallback;
      }

      // Default styled fallback
      return (
        <ErrorFallback
          error={error}
          resetError={this.resetError}
          severity={severity}
          title={title}
          message={message}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
